import React, { useContext } from 'react'
import { Box, Typography, Paper, Avatar, useTheme, useMediaQuery, Button, Skeleton } from '@mui/material'
import FiltrosMetricas from './Filtros'
import { ProductosMasVisitados } from './data'
import BotonPremium from './ButtonPremium'
import { TenantsContext } from '../../../context/tenants.context'
import NoResultsAlert from './NoResultsAlert'

const DataMetricas: React.FC<{
  id: string
  enabled: boolean
  loading: boolean
  title: string
  label: string
  data: any[]
  rubros: any[]
  icono: any
  //iconoTitulo: any
  filtros: { periodo: string; limit: number; miLocal: boolean; codRubro: string }
  setFiltros: (f: { periodo: string; limit: number; miLocal: boolean; codRubro: string }) => void
}> = ({ id, enabled, loading, label, title, data, rubros, icono, filtros, setFiltros }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dataRender = enabled ? data : ProductosMasVisitados
  const { domain } = useContext(TenantsContext)

  const paperSx = {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    gap: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
  }

  if (loading) {
    return (
      <Paper elevation={0} sx={paperSx}>
        {/* Header skeleton */}
        <Typography
          variant="h6"
          sx={{ fontSize: '16px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {icono}
          {title}
        </Typography>
        {id.includes('Prod') && (
          <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: '8px' }} />
          </Box>
        )}
        <Skeleton variant="rounded" width={'100%'} height={32} />

        {/* Filtros skeleton */}
        <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: '8px' }} />
          <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: '8px' }} />
          <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: '8px' }} />
        </Box>
      </Paper>
    )
  }

  if (!dataRender) return null

  return (
    <Paper elevation={0} sx={paperSx}>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{ fontSize: '16px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        <Box component="span" sx={{ display: 'flex', '& img': { width: '18px', height: '18px' } }}>
          {icono}
        </Box>
        {title}
      </Typography>

      <FiltrosMetricas id={id} enabled={enabled} rubros={rubros} filtros={filtros} setFiltros={setFiltros} />
      {dataRender.length === 0 ? (
        <NoResultsAlert />
      ) : (
        <Box
          sx={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: 'column',
            gap: '12px',
            mt: 1,
            '& > *': { minWidth: 0 },
            ...(!isMobile && { gridTemplateColumns: '1fr 1fr', columnGap: '2rem' }),
            opacity: enabled ? 1 : 0.6,
            filter: enabled ? 'none' : 'blur(3px)',
            transition: 'filter 0.2s ease, opacity 0.2s ease',
            pointerEvents: enabled ? 'auto' : 'none'
          }}
        >
          {dataRender?.map((item: any, index: number) => (
            <Box
              key={index}
              onClick={() => {
                id.includes('Prod') && window.open(`https://www.${domain}/p/${item.codProducto}`, '_blank')
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
                cursor: id.includes('Prod') ? 'pointer' : 'default'
              }}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#999', minWidth: '30px' }}>
                #{index + 1}
              </Typography>

              <Avatar
                src={item.urlImagen}
                variant="rounded"
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: '#e0e0e0',
                  borderRadius: '8px',
                  '& img': {
                    objectFit: 'contain',
                    padding: '4px'
                  }
                }}
              >
                {item.nombreLocal
                  ?.split(' ')
                  .filter((word) => isNaN(word.charAt(0)))
                  .slice(0, 2)
                  .map((word) => word.charAt(0).toUpperCase())
                  .join('')}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.nombreLocal || item.nombreRubro}
                </Typography>
                {item.nombreProducto && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#999'
                    }}
                  >
                    {item.nombreProducto}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {icono}
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1976d2', lineHeight: 1.2 }}>
                    {item.cantidad?.toLocaleString('es-AR')}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: '#999' }}>{label}</Typography>
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              display: dataRender.length < 4 ? 'none' : 'flex',
              justifyContent: 'flex-end',
              ...(!isMobile && { gridColumn: '2' })
            }}
          >
            <Button
              sx={{
                backgroundColor: '#5B7FFF',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '15px',
                textTransform: 'none',
                padding: isMobile ? '3px 6px' : '6px 12px',
                borderRadius: '20px',
                '&:hover': { backgroundColor: '#4A6FEE' }
              }}
              onClick={() =>
                filtros.limit === 20
                  ? setFiltros({ ...filtros, limit: isMobile ? 5 : 4 })
                  : setFiltros({ ...filtros, limit: 20 })
              }
            >
              {filtros.limit === 20 ? 'Ver menos' : 'Ver más'}
            </Button>
          </Box>
        </Box>
      )}

      {!enabled && <BotonPremium />}
    </Paper>
  )
}

export default DataMetricas
